import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createConfiguratorBackend,
    deleteConfiguratorBackend,
    getConfiguratorBackends,
    getConfiguratorPreviewRooms,
    getConfiguratorSources,
    getConfiguratorValidationStructure,
    updateConfiguratorBackend,
} from './configurator-backends';
import { ConfiguratorBackendRequest } from './configurator-backends.types';
import { configuratorBackendsKeys } from './configurator-backends.queryKeys';

export function useConfiguratorBackends(projectId: number) {
    return useQuery({
        queryKey: configuratorBackendsKeys.byProject(projectId),
        queryFn: () => getConfiguratorBackends(projectId),
        enabled: !!projectId,
    });
}

export function useConfiguratorSources(projectId: number) {
    return useQuery({
        queryKey: configuratorBackendsKeys.sources(projectId),
        queryFn: () => getConfiguratorSources(projectId),
        enabled: !!projectId,
    });
}

export function useConfiguratorValidationStructure(projectId: number) {
    return useQuery({
        queryKey: configuratorBackendsKeys.validationStructure(projectId),
        queryFn: () => getConfiguratorValidationStructure(projectId),
        enabled: !!projectId,
    });
}

export function useConfiguratorPreviewRooms(projectId: number, projectKey: string | undefined) {
    return useQuery({
        queryKey: configuratorBackendsKeys.previewRooms(projectId, projectKey ?? ''),
        queryFn: () => getConfiguratorPreviewRooms(projectId, projectKey as string),
        enabled: !!projectId && !!projectKey,
    });
}

export function useCreateConfiguratorBackend(projectId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: ConfiguratorBackendRequest) => createConfiguratorBackend(projectId, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: configuratorBackendsKeys.byProject(projectId) });
        },
    });
}

export function useUpdateConfiguratorBackend(projectId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, body }: { id: number; body: ConfiguratorBackendRequest }) =>
            updateConfiguratorBackend(projectId, id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: configuratorBackendsKeys.byProject(projectId) });
        },
    });
}

export function useDeleteConfiguratorBackend(projectId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => deleteConfiguratorBackend(projectId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: configuratorBackendsKeys.byProject(projectId) });
        },
    });
}
